import { useState, useEffect, lazy, Suspense } from "react";
import DropdownInput from '@/components/DropdownInput';

const Chip = lazy(() => import('@mui/material/Chip'));

import { getModalStyle } from '@/utils/Modal';
import { order } from '@/utils/Order';

export default function Dropdown({
    t, translate, type, name, placeholder, 
    value, options, 
    shrink, handleDropdown, error, isFocused,
    disabled,noempty
}){

    const { sxChipOut } = getModalStyle();

    // initially we have all the options but we remove or add to the list if already selected or not
    const [ valuesOptions, setValuesOptions ] = useState(null);

    // the value that you type on the box
    const [ valueTyped, setValueTyped ] = useState(/*type && type === 'multiple' ? null : value*/'');

    // the values that are already chosen
    const [ valuesChosen, setValuesChosen ] = useState(/*type && type === 'multiple' ? value : null*/null);

    useEffect(() => {

        // set options removing the chosen values
        if(type && type === 'multiple'){

            setValueTyped(null);
            setValuesChosen(value) 

            const chosenIds = new Set(value.map(item => item.id));
            setValuesOptions(options.filter((v) => !chosenIds.has(v.id)));
        }
        else{
            setValueTyped(value);
            setValuesChosen(null);
            setValuesOptions(options);
        }
        
    }, [value, options]);

    // to autocomplete when typing  
    const [ inputValueAutocomplete, setInputValueAutocomplete ] = useState('');

    const handleInputValueAutocomplete = (newValue) => {
        setInputValueAutocomplete(newValue);
    }

    // {value: X, label: Y'}
    // when you click on an item of the list
    // reason can be selectOption or clear when using backspace    
    const handleValueTyped = (e,newValue,reason = 'selectOption') => {
        
        // remove the clear with backspace because it does not work properly
        // it deletes the item when still one character remaining
        if(reason === 'clear'){
            return;
        }

        if(type && type === 'multiple'){
            
            // multiple
            let newValuesChosen;
            if(!valuesChosen){
                newValuesChosen = [newValue];
            }
            else{
                newValuesChosen = [...valuesChosen,newValue];
            }

            // order alphabetically
            newValuesChosen.sort(order('asc','name','string'));
            setValuesChosen(newValuesChosen);
            handleDropdown(newValuesChosen);

            // remove from options to select
            setValuesOptions(valuesOptions.filter((v) => v.id !== newValue.id));

            setValueTyped(null);
            setInputValueAutocomplete('')
        }
        else{
            if(type && type === 'search'){
                setValueTyped(newValue);
                handleDropdown(newValue);
            }
            else{
                // single                   
                var newId = newValue?.props?.value;
                //var newName = newValue?.props?.children;
                setValueTyped(newId);
                handleDropdown(newId);
            }
        }
    }

    // if typing part of the name and clicking enter, add it
    // or if only one option on the list, add it
    const handleKey = (e) => {

        // key === 'Enter'
        if(e.keyCode === 13){ 
            
            // check if value on list of options and add it    
            var string = e.target.value.toLowerCase();
            var included = false;
            var optionsFiltered = 0;
            var optionsFilteredValue = false;
            
            valuesOptions?.map((val) => {

                // check if value matches an option
                if(val?.name?.toLowerCase() === string){
                    included = val;
                    return;
                }

                // check if string is part of one of the options and there is only one
                // if so, get it
                if(optionsFiltered <= 1 && val?.name?.toLowerCase().includes(string)){
                    optionsFilteredValue = val;
                    optionsFiltered++;
                }               
            });

            // if on the list, add it
            if(included){
                handleValueTyped(e,included);
            }
            else{
                // if string is part of one of the options and there is only one, add it
                if(optionsFiltered === 1){
                    handleValueTyped(e,optionsFilteredValue);
                }
            }
        }
        else{
            // key === <- BACKSPACE
            if(e.keyCode === 8){                 

                // e.target.value is the string that is already written
                // for example xyz, if you delete z, e.target.value is xy
                // so when empty string, it means all deleted and remove selected item    
                if(!e.target.value || e.target.value.length === 0){  

                    /*iftype && type === 'multiple'){
                    }
                    else{
                    */
                    if(type && type === 'search'){
                        //if(type === 'search'){
                            handleValueDelete(e,valueTyped);
                        //}
                    }
                }
            }
        }
    };

    const handleValueDelete = (e,item) => {

        if(type && type === 'multiple'){
            
            // remove from chosen values
            var newValuesChosen = valuesChosen.filter((v) => v.id !== item.id);
            setValuesChosen(newValuesChosen);
            handleDropdown(newValuesChosen);

            // order the list of options alphabetically adding the deleted item
            var orderOptions = [...valuesOptions,item];
            orderOptions.sort(order('asc','name','string'));
            setValuesOptions(orderOptions);
        }
        else{
            setValueTyped(null);
            handleDropdown(null);
        }
    };
    
    return (
        <>
        <DropdownInput
            t={t}
            translate={translate}
            size='small'
            key={'name_'+name}
            id={'name_'+name}
            name={name}                        
            value={valueTyped}
            values={valuesChosen}
            onChange={(e,value,reason) => handleValueTyped(e,value,reason)}
            inputValue={inputValueAutocomplete}
            onInputChange={(e,value) => handleInputValueAutocomplete(value)}
            handleValueDelete={handleValueDelete}
            onKeyDown={handleKey}
            options={valuesOptions}
            placeholder={placeholder}
            shrink={shrink}
            error={error}
            multiple={type && type === 'multiple' ? true : false}
            autoComplete={type && type === 'multiple' ? false : type && type === 'search' ? true : false }
            norender={type && type === 'multiple' ? true : false } // do not show the chips inside the select
            isFocused={isFocused}
            disabled={disabled}
            noempty={noempty}
        />      
        <div className='mx-2'>
        {
            type && type === 'multiple' &&
            valuesChosen &&
            valuesChosen?.map((item) => (
                <span key={name+'_'+item?.id}>
                    <Suspense>
                        <Chip                            
                            sx={sxChipOut}                            
                            label={
                                item?.name ?                                         
                                    t && translate ?
                                        t(translate+'.'+item.name)
                                    :
                                        item.name
                                :
                                    item?.name
                            }
                            variant="outlined"
                            onClick={() => {}}
                            onDelete={(e) => handleValueDelete(e,item)}
                        />
                    </Suspense>
                    <br/>
                </span>  
            ))
        }
        </div>
        </>
  );
}